import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Ban, Building2, Check, Circle, Copy, KeyRound, Link2Off, Loader2, Lock, Mail, PenLine, RefreshCw, Trash2, UserPlus, Users } from "lucide-react";
import ActivityPanel from "@/components/ActivityPanel";
import { PasswordInput } from "@/pages/Login";
import { api, formatDateTime } from "@/api";
import { useAuth } from "@/auth";

// Administrator only (schalk@ - invited users are never administrators). Owner (2026-10-11): the B-BBEE
// site's Settings page (pages/Settings.jsx there), the same users table, invitations and password rules.
// Owner's rules: invitation only; the administrator sets every password (and sends it to the user by
// email / WhatsApp); MFA is mandatory - switching it off also disables the account, switching it on
// again re-enables the account and the user sets up MFA afresh.

// Owner's password rules (2026-10-10) - the backend checks the same (auth.password_problem)
const MIN_PASSWORD_LENGTH = 10;
const PASSWORD_RULES = [
  { label: `At least ${MIN_PASSWORD_LENGTH} characters`, test: (p) => p.length >= MIN_PASSWORD_LENGTH },
  { label: "At least one capital letter", test: (p) => /\p{Lu}/u.test(p) },
  { label: "At least one digit", test: (p) => /\p{Nd}/u.test(p) },
  { label: "At least one special character (such as ! @ # $ % - _)", test: (p) => /[^\p{L}\p{N}\s]/u.test(p) },
];
const passwordOk = (p) => PASSWORD_RULES.every((r) => r.test(p));

// No 0/O, 1/I/L: easy to read out or type from a message
const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

// 4 groups of 5 joined by "-" (the special character); drawn again until it has a capital and a digit
const generatePassword = () => {
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint32Array(20));
    const chars = Array.from(bytes, (b) => ALPHABET[b % ALPHABET.length]).join("");
    const password = chars.match(/.{5}/g).join("-");
    if (passwordOk(password)) return password;
  }
};

const MFA_LABELS = { active: "Active", pending: "Pending", off: "Off" };
const MFA_HINTS = {
  active: "MFA is set up",
  pending: "Will set up MFA at the next sign-in",
  off: "MFA is off, so the account can't be used",
};

// tone (owner, 2026-10-10): on = green (knob right); pending = orange and off = red (knob left) - an MFA
// still to be set up must not look active
// locked (owner, 2026-10-10): MFA Pending can't be clicked - MFA doesn't exist yet; it stays full orange
// (a disabled switch would fade)
function Switch({ checked, onChange, disabled, locked, label, testId, tone = checked ? "on" : "off" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      className={`tca-switch is-${tone}${locked ? " is-locked" : ""}`}
      aria-disabled={locked || undefined}
      onClick={() => { if (!locked) onChange(!checked); }}
      disabled={disabled}
      data-testid={testId}
    >
      <span className="tca-switch-knob" />
    </button>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="tca-btn-small"
      onClick={() => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />} <span>{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

// Password field with a Generate button (the administrator chooses or generates every password)
function PasswordField({ id, value, onChange }) {
  return (
    <>
      <label className="tca-label" htmlFor={id}>Password</label>
      <div className="tca-password-row">
        <PasswordInput id={id} value={value} onChange={onChange} autoComplete="new-password" placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`} />
        <button type="button" className="tca-btn-small" onClick={() => onChange(generatePassword())} data-testid={`${id}-generate`}>
          <RefreshCw size={14} /> <span>Generate</span>
        </button>
      </div>
      {/* The requirements, ticked off as the password meets each one */}
      <ul className="tca-pw-rules" aria-label="Password requirements" data-testid={`${id}-rules`}>
        {PASSWORD_RULES.map((r) => {
          const ok = r.test(value);
          return (
            <li key={r.label} className={ok ? "is-met" : undefined}>
              {ok ? <Check size={14} className="tca-pw-rule-icon" /> : <Circle size={14} className="tca-pw-rule-icon" />}
              <span>{r.label}</span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function Modal({ title, children, onClose, testId, wide }) {
  return (
    <div className="modal-overlay" onClick={onClose} data-testid={testId}>
      <div className={`modal-content tca-settings-modal${wide ? " is-wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function InviteModal({ onClose, onInvited }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const ready = form.first_name.trim() && form.last_name.trim() && form.email.trim() && passwordOk(form.password);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api("/admin/users", { method: "POST", body: form });
      onInvited(result, form.password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Invite New User" onClose={onClose} testId="invite-dialog">
      <form onSubmit={submit} noValidate>
        <p className="modal-description">
          The person receives an invitation email: their username is their email address, and their password will be
          sent to them separately by you.
        </p>
        <div className="tca-form-pair">
          <div>
            <label className="tca-label" htmlFor="invite-first">Name</label>
            <input id="invite-first" className="tca-input" value={form.first_name} onChange={(e) => set("first_name")(e.target.value)} autoFocus autoComplete="off" />
          </div>
          <div>
            <label className="tca-label" htmlFor="invite-last">Surname</label>
            <input id="invite-last" className="tca-input" value={form.last_name} onChange={(e) => set("last_name")(e.target.value)} autoComplete="off" />
          </div>
        </div>
        <label className="tca-label" htmlFor="invite-email">Email Address</label>
        <input id="invite-email" className="tca-input" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} autoComplete="off" />
        <PasswordField id="invite-password" value={form.password} onChange={set("password")} />
        {error && <div className="tca-auth-error" role="alert">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="tca-btn-plain" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-shimmer-orange tca-btn-done" disabled={!ready || busy} data-testid="button-invite-send">
            <span className="tca-inline">{busy ? <Loader2 size={16} className="tca-spin" /> : <Mail size={16} />} Send Invitation</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Edit User (owner, 2026-10-11): fix the name / surname or change the email address (their username).
// Password (Reset Password) and MFA stay; the invitation is not sent again automatically.
function EditUserModal({ user, isMe, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name: user.first_name, last_name: user.last_name, email: user.email });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const emailChanged = form.email.trim().toLowerCase() !== user.email;
  const nameChanged = form.first_name.trim() !== user.first_name || form.last_name.trim() !== user.last_name;
  const ready = form.first_name.trim() && form.last_name.trim() && form.email.trim() && (emailChanged || nameChanged);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api(`/admin/users/${user.id}`, { method: "PUT", body: form });
      onSaved(result.user, emailChanged);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit User" onClose={onClose} testId="edit-dialog">
      <form onSubmit={submit} noValidate>
        <p className="modal-description">
          Change <strong>{user.first_name} {user.last_name}</strong>'s details. Their password and MFA stay the same.
        </p>
        <div className="tca-form-pair">
          <div>
            <label className="tca-label" htmlFor="edit-first">Name</label>
            <input id="edit-first" className="tca-input" value={form.first_name} onChange={set("first_name")} autoFocus autoComplete="off" />
          </div>
          <div>
            <label className="tca-label" htmlFor="edit-last">Surname</label>
            <input id="edit-last" className="tca-input" value={form.last_name} onChange={set("last_name")} autoComplete="off" />
          </div>
        </div>
        <label className="tca-label" htmlFor="edit-email">Email Address</label>
        <input id="edit-email" className="tca-input" type="email" value={form.email} onChange={set("email")} autoComplete="off" />
        {emailChanged && (
          <p className="tca-field-hint" data-testid="edit-email-note">
            The email address is their username: {isMe ? "you sign in with the new address next time (you stay signed in here)" : "they are signed out and sign in with the new address next time"}.
            The invitation isn't sent automatically - use <strong>Resend Invitation</strong> if they need it.
          </p>
        )}
        {error && <div className="tca-auth-error" role="alert">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="tca-btn-plain" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-shimmer-orange tca-btn-done" disabled={!ready || busy} data-testid="button-edit-save">
            <span className="tca-inline">{busy ? <Loader2 size={16} className="tca-spin" /> : <Check size={16} />} Save Changes</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PasswordModal({ user, isMe, onClose, onSaved }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api(`/admin/users/${user.id}/password`, { method: "POST", body: { password } });
      onSaved(result.user, password);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Reset Password" onClose={onClose} testId="password-dialog">
      <form onSubmit={submit} noValidate>
        <p className="modal-description">
          New password for <strong>{user.first_name} {user.last_name}</strong> (<strong className="tca-strong-underline">{user.email}</strong>).
        </p>
        {isMe ? (
          // The administrator's own reset keeps this session (backend re-issues it)
          <p className="modal-description">
            You stay signed in here, and are signed out on all your other devices.
          </p>
        ) : (
          <p className="modal-description">
            They are signed out everywhere on all devices and all pages on this website. Remember to send them the new
            password yourself via email, SMS, or WhatsApp (whichever you prefer).
          </p>
        )}
        <PasswordField id="reset-password" value={password} onChange={setPassword} />
        {error && <div className="tca-auth-error" role="alert">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="tca-btn-plain" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-shimmer-orange tca-btn-done" disabled={!passwordOk(password) || busy} data-testid="button-password-save">
            <span className="tca-inline">{busy ? <Loader2 size={16} className="tca-spin" /> : <KeyRound size={16} />} Save Password</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

// After Connect Xero, Xero sends the browser back to /settings?xero=<result> (backend xero.py callback)
const XERO_RESULTS = {
  connected: { ok: true, text: "TC Auditors' Xero is connected." },
  cancelled: { ok: false, text: "Xero wasn't connected: the authorisation was cancelled." },
  expired: { ok: false, text: "That took too long. Please click Connect Xero again." },
  "not-admin": { ok: false, text: "Only an active administrator account can connect Xero." },
  "choose-one": { ok: false, text: "Please tick only TC Auditors' own organisation on Xero's screen, then try again." },
  failed: { ok: false, text: "Xero couldn't be connected. Please try again." },
};

// Blocked organisations (owner, 2026-10-11): clients TC Auditors no longer serves - ONE list shared with the
// B-BBEE site (bee.tcauditors.co.za): blocking or unblocking here applies there too, and the other way round.
function BlockedPanel({ onConfirm }) {
  const [list, setList] = useState(null);
  const [orgs, setOrgs] = useState(null); // the whole contact group, for choosing one to block
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/xero/blocked").then((d) => setList(d.blocked)).catch((err) => setError(err.message));
  }, []);

  const loadOrgs = async () => {
    setBusy(true);
    setError("");
    try {
      setOrgs((await api("/xero/organisations")).organisations);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    setBusy(true);
    setError("");
    try {
      const d = await api("/xero/blocked", { method: "POST", body: { contact_id: choice } });
      setList(d.blocked);
      setOrgs((o) => o.map((x) => (x.id === choice ? { ...x, blocked: true } : x)));
      setChoice("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const unblock = (b) => onConfirm({
    title: "Unblock Organisation",
    text: `${b.name} can be chosen again, on this site and on the B-BBEE Affidavit Registry.`,
    action: async () => {
      try {
        const d = await api(`/xero/blocked/${b.contact_id}`, { method: "DELETE" });
        setList(d.blocked);
        setOrgs((o) => o && o.map((x) => (x.id.toLowerCase() === b.contact_id ? { ...x, blocked: false } : x)));
      } catch (err) {
        setError(err.message);
      }
    },
  });

  const available = (orgs || []).filter((o) => !o.blocked);

  return (
    <section className="tca-settings-panel tca-xero-panel" aria-labelledby="blocked-title" data-testid="blocked-panel">
      <div className="tca-settings-head">
        <h2 id="blocked-title"><Ban size={20} /> Blocked Organisations</h2>
      </div>
      <p className="tca-settings-intro">
        Clients TC Auditors no longer serves. They won't appear in the checklist's client list. This is the{" "}
        <strong>same list as on the B-BBEE Affidavit Registry</strong>: blocking or unblocking here applies there too
        (and the other way round).
      </p>

      <div className="tca-blocked-add">
        {orgs === null ? (
          <button type="button" className="tca-btn-small" onClick={loadOrgs} disabled={busy} data-testid="button-blocked-load">
            {busy ? <Loader2 size={14} className="tca-spin" /> : <RefreshCw size={14} />} <span>Load the Xero list to block an organisation</span>
          </button>
        ) : (
          <>
            <select className="tca-input tca-select" value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Organisation to block" data-testid="select-block">
              <option value="">Choose the organisation to block</option>
              {available.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button type="button" className="btn-shimmer-orange tca-btn-done" onClick={block} disabled={!choice || busy} data-testid="button-block">
              <span className="tca-inline"><Ban size={16} /> Block</span>
            </button>
          </>
        )}
      </div>

      {error && <div className="tca-auth-error" role="alert">{error}</div>}

      {!list ? (
        !error && <div className="tca-settings-loading"><Loader2 size={22} className="tca-spin" /></div>
      ) : list.length === 0 ? (
        <p className="tca-blocked-empty">No organisations are blocked.</p>
      ) : (
        <table className="tca-users" data-testid="blocked-table">
          <thead>
            <tr>
              <th>Organisation</th>
              <th>Blocked By</th>
              <th>Blocked On</th>
              <th aria-label="Unblock" />
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.contact_id} data-testid={`blocked-row-${b.contact_id}`}>
                <td data-label="Organisation"><div className="tca-user-name">{b.name}</div></td>
                <td data-label="Blocked By" className="tca-user-last">{b.blocked_by}</td>
                <td data-label="Blocked On" className="tca-user-last">{formatDateTime(b.blocked_at)}</td>
                <td data-label="">
                  <button type="button" className="tca-btn-small" onClick={() => unblock(b)} data-testid={`button-unblock-${b.contact_id}`}>
                    <span>Unblock</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// The Xero app "Xero Setup Checklist" (owner, 2026-10-11): 5 connections. 1. TC Auditors' own organisation -
// permanent, connected here (its contacts: the checklist's client list, to be built, and Blocked
// Organisations). 2. A client's organisation - one setup at a time, connected and disconnected again by the
// checklist page (to be built); the second panel shows any still open, with Disconnect as a safety net.
function XeroPanels({ onConfirm }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(() => XERO_RESULTS[new URLSearchParams(location.search).get("xero")] || null);
  const [clientMessage, setClientMessage] = useState(null);

  const load = useCallback(() => {
    api("/xero/status").then(setStatus).catch((err) => setMessage({ ok: false, text: err.message }));
  }, []);
  useEffect(load, [load]);
  // The result is shown once; the address goes back to plain /settings
  useEffect(() => {
    if (location.search.includes("xero=")) navigate("/settings", { replace: true });
  }, [location.search, navigate]);

  const connect = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { url } = await api("/xero/connect", { method: "POST" });
      window.location.assign(url); // Xero's own sign-in and authorisation screen
    } catch (err) {
      setMessage({ ok: false, text: err.message });
      setBusy(false);
    }
  };

  const disconnect = () => onConfirm({
    title: "Disconnect TC Auditors' Xero",
    text: "The client list and Blocked Organisations' Xero list stop working until TC Auditors' Xero is connected again.",
    action: async () => {
      try {
        await api("/xero/disconnect", { method: "POST" });
        setMessage({ ok: true, text: "TC Auditors' Xero is disconnected." });
        load();
      } catch (err) {
        setMessage({ ok: false, text: err.message });
      }
    },
  });

  const disconnectClient = (c) => onConfirm({
    title: "Disconnect Client's Xero",
    text: `${c.tenant_name}'s Xero is disconnected from this site and its connection slot is freed. Nothing in their Xero changes.`,
    action: async () => {
      try {
        setStatus(await api(`/xero/clients/${encodeURIComponent(c.tenant_id)}`, { method: "DELETE" }));
        setClientMessage({ ok: true, text: `${c.tenant_name}'s Xero is disconnected.` });
      } catch (err) {
        setClientMessage({ ok: false, text: err.message });
        load();
      }
    },
  });

  return (
    <>
      <section className="tca-settings-panel tca-xero-panel" aria-labelledby="xero-title" data-testid="xero-panel">
        <div className="tca-settings-head">
          <h2 id="xero-title"><Building2 size={20} /> TC Auditors' Xero</h2>
        </div>
        <p className="tca-settings-intro">
          TC Auditors' own Xero organisation, connected once and kept connected (read-only access to its contacts). The
          checklist's client list will come from its <strong>{status?.group || "MONTHLY RETAINERS"}</strong> contact
          group, and Blocked Organisations chooses from it. On Xero's screen, tick <strong>only TC Auditors' own
          organisation</strong>.
        </p>
        {!status ? (
          <div className="tca-settings-loading"><Loader2 size={22} className="tca-spin" /></div>
        ) : !status.configured ? (
          <p className="tca-auth-error" data-testid="xero-not-configured">
            The Xero app's keys aren't on the server yet: add <code>XERO_CLIENT_ID</code> and <code>XERO_CLIENT_SECRET</code> to
            the GitHub secrets and deploy.
          </p>
        ) : (
          <div className="tca-xero-status">
            <span className={`tca-status ${status.connected ? "is-ok" : "is-off"}`} data-testid="xero-status">
              {status.connected ? "Connected" : "Not connected"}
            </span>
            {status.connected && (
              <span className="tca-xero-org">
                <strong>{status.tenant_name}</strong> &middot; by {status.connected_by} on {formatDateTime(status.connected_at)}
              </span>
            )}
            <span className="tca-xero-buttons">
              <button type="button" className="btn-shimmer-orange tca-btn-done" onClick={connect} disabled={busy} data-testid="button-xero-connect">
                <span className="tca-inline">{busy ? <Loader2 size={16} className="tca-spin" /> : <RefreshCw size={16} />} {status.connected ? "Connect Again" : "Connect Xero"}</span>
              </button>
              {status.connected && (
                <button type="button" className="tca-btn-small" onClick={disconnect} data-testid="button-xero-disconnect">Disconnect</button>
              )}
            </span>
          </div>
        )}
        {message && (
          <p className={message.ok ? "tca-ok" : "tca-field-hint is-error"} data-testid="xero-message">{message.text}</p>
        )}
      </section>

      <section className="tca-settings-panel tca-xero-panel" aria-labelledby="clients-title" data-testid="client-connections-panel">
        <div className="tca-settings-head">
          <h2 id="clients-title"><Link2Off size={20} /> Client Xero Connections</h2>
          {status && <span className="tca-slots" data-testid="xero-slots">{status.in_use} of {status.limit} connections in use</span>}
        </div>
        <p className="tca-settings-intro">
          A client's Xero is connected for one setup only: the checklist connects it, updates its settings and chart of
          accounts, and disconnects it again when the setup is finished. Any client connection still open shows here -
          <strong> Disconnect</strong> frees its slot (the Xero app allows {status?.limit || 5} connections, TC Auditors'
          own included).
        </p>
        {status && (status.clients.length === 0 ? (
          <p className="tca-blocked-empty" data-testid="no-client-connections">No client's Xero is connected at the moment.</p>
        ) : (
          <ul className="tca-clients" data-testid="client-connections">
            {status.clients.map((c) => (
              <li key={c.tenant_id}>
                <span className="tca-status is-pending">Connected</span>
                <span className="tca-xero-org"><strong>{c.tenant_name}</strong> &middot; by {c.connected_by} on {formatDateTime(c.connected_at)}</span>
                <span className="tca-xero-buttons">
                  <button type="button" className="tca-btn-small tca-btn-danger" onClick={() => disconnectClient(c)} data-testid={`button-client-disconnect-${c.tenant_id}`}>
                    <Link2Off size={14} /> <span>Disconnect</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ))}
        {clientMessage && (
          <p className={clientMessage.ok ? "tca-ok" : "tca-field-hint is-error"} data-testid="client-message">{clientMessage.text}</p>
        )}
      </section>
    </>
  );
}

export default function Settings() {
  const { user: me, setUser: setMe } = useAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { title, text, action }
  const [notice, setNotice] = useState(null);   // { title, text, password? }

  const load = useCallback(() => {
    api("/admin/users").then(setUsers).catch((err) => setError(err.message));
  }, []);
  useEffect(load, [load]);

  const replace = (u) => setUsers((list) => list.map((x) => (x.id === u.id ? u : x)));

  const update = async (u, body) => {
    setBusyId(u.id);
    setError("");
    try {
      replace((await api(`/admin/users/${u.id}`, { method: "PATCH", body })).user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleAccount = (u, on) => {
    if (on) return update(u, { active: true });
    setConfirm({
      title: "Disable Account",
      text: `${u.first_name} ${u.last_name} will be signed out and won't be able to sign in until you enable the account again.`,
      action: () => update(u, { active: false }),
    });
  };

  const toggleMfa = (u, on) => {
    if (on) {
      return setConfirm({
        title: "Switch MFA On",
        text: `${u.first_name} ${u.last_name}'s account is enabled again. They set up MFA afresh the next time they sign in.`,
        action: () => update(u, { mfa_enabled: true }),
      });
    }
    setConfirm({
      title: "Switch MFA Off",
      text: `MFA is required to use this site, so switching it off also disables ${u.first_name} ${u.last_name}'s account. Switching MFA on again later enables the account, and they set up MFA afresh.`,
      action: () => update(u, { mfa_enabled: false }),
    });
  };

  const deleteUser = (u) => setConfirm({
    title: "Delete User",
    text: `${u.first_name} ${u.last_name} (${u.email}) will be removed completely and signed out at once. This can't be undone - to give them access again, invite them as a new user. Their past activity stays in the activity log.`,
    action: async () => {
      setBusyId(u.id);
      setError("");
      try {
        await api(`/admin/users/${u.id}`, { method: "DELETE" });
        setUsers((list) => list.filter((x) => x.id !== u.id));
        setNotice({ title: "User Deleted", text: `${u.first_name} ${u.last_name} was deleted.` });
      } catch (err) {
        setError(err.message);
      } finally {
        setBusyId(null);
      }
    },
  });

  const simpleAction = async (u, path, done) => {
    setBusyId(u.id);
    setError("");
    try {
      const result = await api(`/admin/users/${u.id}/${path}`, { method: "POST" });
      if (result.user) replace(result.user);
      done?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  // At the top, and again at the bottom above "Signed in as" (owner, 2026-10-11, as Back To Affidavits on the
  // B-BBEE site). The checklist's progress is kept while Settings is open.
  const back = (place) => (
    <Link to="/" className="btn-shimmer-orange tca-back-btn" data-testid={place === "top" ? "link-back-checklist" : "link-back-checklist-bottom"}>
      <ArrowLeft className="w-5 h-5" /> <span>Back To Checklist</span>
    </Link>
  );

  return (
    <>
      <div className="tca-page-top">{back("top")}</div>

      <section className="tca-settings-panel" aria-labelledby="users-title">
        <div className="tca-settings-head">
          <h2 id="users-title"><Users size={20} /> Users</h2>
          <button type="button" className="btn-shimmer-orange tca-btn-done" onClick={() => setInviting(true)} data-testid="button-invite">
            <span className="tca-inline"><UserPlus size={16} /> Invite New User</span>
          </button>
        </div>
        <p className="tca-settings-intro">
          The site is invitation only and two-factor authentication (MFA) is mandatory. MFA <strong>Pending</strong> means
          the user hasn't signed in and set up MFA yet; <strong>Active</strong> means MFA is set up.
        </p>

        {error && <div className="tca-auth-error" role="alert">{error}</div>}

        {!users ? (
          <div className="tca-settings-loading"><Loader2 size={24} className="tca-spin" /></div>
        ) : (
          <table className="tca-users" data-testid="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Edit</th>
                <th>Account</th>
                <th>MFA</th>
                <th>Password</th>
                <th>Last Sign-In</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const self = u.id === me.id;
                const busy = busyId === u.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className={u.active ? "" : "is-disabled"}>
                    <td data-label="User">
                      <div className="tca-user-name">
                        {u.first_name} {u.last_name}
                        {u.role === "admin" && <span className="tca-badge is-admin">Admin</span>}
                        {self && <span className="tca-badge">You</span>}
                        {u.locked && <span className="tca-badge is-locked"><Lock size={11} /> Locked</span>}
                      </div>
                      <div className="tca-user-email">{u.email}</div>
                    </td>
                    <td data-label="Edit">
                      <div className="tca-row-actions">
                        <button
                          type="button"
                          className="tca-btn-small tca-btn-danger"
                          onClick={() => deleteUser(u)}
                          disabled={self || busy}
                          title={self ? "You can't delete your own account" : "Remove this user completely"}
                          data-testid={`button-delete-${u.email}`}
                        >
                          <Trash2 size={14} /> <span>Delete User</span>
                        </button>
                        <button type="button" className="tca-btn-small" onClick={() => setEditing(u)} disabled={busy} data-testid={`button-edit-${u.email}`}>
                          <PenLine size={14} /> <span>Edit User</span>
                        </button>
                      </div>
                    </td>
                    <td data-label="Account">
                      <div className="tca-toggle-cell">
                        <Switch
                          checked={u.active}
                          onChange={(on) => toggleAccount(u, on)}
                          disabled={self || busy}
                          label={self ? "You can't disable your own account" : u.active ? "Disable this account" : "Enable this account"}
                          testId={`toggle-account-${u.email}`}
                        />
                        <span className={`tca-status ${u.active ? "is-ok" : "is-off"}`}>{u.active ? "Active" : "Disabled"}</span>
                      </div>
                    </td>
                    <td data-label="MFA">
                      <div className="tca-toggle-cell">
                        <Switch
                          checked={u.mfa_status === "active"}
                          tone={u.mfa_status === "active" ? "on" : u.mfa_status}
                          // Pending (knob left, orange): can't be clicked (owner) - it turns green once the user sets up MFA
                          onChange={() => toggleMfa(u, u.mfa_status === "off")}
                          locked={u.mfa_status === "pending"}
                          disabled={self || busy}
                          label={self ? "You can't switch off your own MFA" : u.mfa_status === "off" ? "Switch MFA on" : u.mfa_status === "pending" ? "MFA pending: set up by the user at their first sign-in" : "Switch MFA off"}
                          testId={`toggle-mfa-${u.email}`}
                        />
                        <span className={`tca-status is-${u.mfa_status}`} title={MFA_HINTS[u.mfa_status]} data-testid={`mfa-status-${u.email}`}>
                          {MFA_LABELS[u.mfa_status]}
                        </span>
                      </div>
                    </td>
                    <td data-label="Password">
                      <div className="tca-row-actions">
                        <button type="button" className="tca-btn-small" onClick={() => setResetting(u)} disabled={busy} data-testid={`button-reset-${u.email}`}>
                          <KeyRound size={14} /> <span>Reset Password</span>
                        </button>
                        {u.locked && (
                          <button type="button" className="tca-btn-small" onClick={() => simpleAction(u, "unlock")} disabled={busy}>
                            <Lock size={14} /> <span>Unlock</span>
                          </button>
                        )}
                        {u.invite_due && !self && (
                          <button
                            type="button"
                            className="tca-btn-small"
                            disabled={busy}
                            onClick={() => simpleAction(u, "resend-invitation", () => setNotice({ title: "Invitation Sent", text: `The invitation was sent to ${u.email} again.` }))}
                          >
                            <Mail size={14} /> <span>Resend Invitation</span>
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Date and time on two lines (owner, 2026-10-11: a narrower column - room for the emails) */}
                    <td data-label="Last Sign-In" className="tca-user-last">
                      {u.last_login ? (
                        <>
                          <span>{new Date(u.last_login).toLocaleDateString("en-ZA", { dateStyle: "medium" })}</span>{" "}
                          <span>{new Date(u.last_login).toLocaleTimeString("en-ZA", { timeStyle: "short" })}</span>
                        </>
                      ) : "Never"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Owner (2026-10-11): Recent Activity below the users */}
      <ActivityPanel />
      <XeroPanels onConfirm={setConfirm} />
      <BlockedPanel onConfirm={setConfirm} />

      <div className="tca-page-bottom">{back("bottom")}</div>

      {inviting && (
        <InviteModal
          onClose={() => setInviting(false)}
          onInvited={(result, password) => {
            setInviting(false);
            setUsers((list) => [...list, result.user].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)));
            setNotice({
              title: result.invitation_sent ? "Invitation Sent" : "User Added - Email Not Sent",
              text: result.invitation_sent
                ? `${result.user.first_name} ${result.user.last_name} has been invited (${result.user.email}). Remember to send them their password separately.`
                : `${result.user.first_name} ${result.user.last_name} was added, but the invitation email could not be sent. Use Resend Invitation, and send them their password separately.`,
              password,
            });
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          isMe={editing.id === me.id}
          onClose={() => setEditing(null)}
          onSaved={(u, emailChanged) => {
            setEditing(null);
            if (u.id === me.id) setMe((m) => ({ ...m, ...u }));
            setUsers((list) => list.map((x) => (x.id === u.id ? u : x))
              .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)));
            setNotice({
              title: "User Updated",
              text: emailChanged && u.id !== me.id
                ? `${u.first_name} ${u.last_name}'s details are saved. Their username is now ${u.email}: they were signed out and sign in with the new address. Use Resend Invitation if they need the invitation again.`
                : `${u.first_name} ${u.last_name}'s details are saved.`,
            });
          }}
        />
      )}

      {resetting && (
        <PasswordModal
          user={resetting}
          isMe={resetting.id === me.id}
          onClose={() => setResetting(null)}
          onSaved={(u, password) => {
            setResetting(null);
            replace(u);
            setNotice({
              title: "Password Saved",
              text: u.id === me.id
                ? "Your new password is saved."
                : `The new password for ${u.first_name} ${u.last_name} is saved. Send it to them yourself.`,
              password,
            });
          }}
        />
      )}

      {confirm && (
        <Modal title={confirm.title} onClose={() => setConfirm(null)} testId="confirm-dialog">
          <p className="modal-description">{confirm.text}</p>
          <div className="modal-actions">
            <button type="button" className="tca-btn-plain" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className="send-btn" onClick={() => { confirm.action(); setConfirm(null); }} data-testid="button-confirm">Confirm</button>
          </div>
        </Modal>
      )}

      {notice && (
        <Modal title={notice.title} onClose={() => setNotice(null)} testId="notice-dialog">
          <p className="modal-description">{notice.text}</p>
          {notice.password && (
            <div className="tca-password-show">
              <code>{notice.password}</code>
              <CopyButton text={notice.password} />
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="send-btn" onClick={() => setNotice(null)}>OK</button>
          </div>
        </Modal>
      )}
    </>
  );
}
